import React, {useEffect, useRef, useState} from 'react';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {Button, Chip, CircularProgress, DialogContent, IconButton, TextField, Tooltip, Typography} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Skeleton from '@material-ui/lab/Skeleton';
import AttachmentIcon from '@material-ui/icons/Attachment';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import {red} from '@material-ui/core/colors';
import moment from 'moment';
import {saveAs} from 'file-saver';
import API_Service, {axiosURIencode} from '../../Services/API_Service';
import {
    req_manage_users_attachment,
    req_manage_users_attachment_download,
    req_manage_users_attachment_rotate
} from '../../Helper/Constants';
import AnyDialog from '../AnyDialog';
import LinearLoading from '../LinearLoading';
import AttachmentViewer, {getPreviewKind} from '../AttachmentViewer';

const MAX_FILE_BYTES = 10485760;
const ROW_COLUMNS = 'minmax(0,2.8fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.7fr) 132px';

export const formatFileSize = (bytes) => {
    var n = parseInt(bytes, 10);
    if (isNaN(n) || n <= 0) return '0 KB';
    if (n >= 1048576) {
        return (n / 1048576).toFixed(1).replace(/\.0$/, '') + ' MB';
    }
    return Math.max(1, Math.round(n / 1024)) + ' KB';
};

export const fileExt = (name) => {
    var s = String(name || '');
    var idx = s.lastIndexOf('.');
    if (idx <= 0 || idx === s.length - 1) return 'FILE';
    return s.substring(idx + 1).toUpperCase().substring(0, 5);
};

export const formatUploadedOn = (value) => {
    if (!value) return '-';
    var m = moment(value, 'YYYY-MM-DD HH:mm:ss');
    return m.isValid() ? m.format('DD MMM YYYY') : '-';
};

const useStyles = makeStyles((theme) => {
    const dark = theme.palette.type === 'dark';
    const accent = dark ? '#FEF200' : '#9c8f00';
    const line = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
    const tile = dark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)';
    const tileBorder = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const textMain = dark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.82)';
    const textMuted = dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
    return {
        header: {
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
        },
        countChip: {
            marginLeft: theme.spacing(1.25),
            height: 22,
            fontSize: 11,
            fontWeight: 700,
        },
        uploadBtn: {
            backgroundColor: '#FEF200',
            color: '#1a1a1a',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
                backgroundColor: '#f0e400',
            },
        },
        listHeader: {
            display: 'grid',
            gridTemplateColumns: ROW_COLUMNS,
            gap: theme.spacing(1.5),
            alignItems: 'center',
            padding: theme.spacing(0, 1.5, 0.5),
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },
        headerRight: {
            textAlign: 'right',
        },
        row: {
            display: 'grid',
            gridTemplateColumns: ROW_COLUMNS,
            gap: theme.spacing(1.5),
            alignItems: 'center',
            borderRadius: 10,
            padding: theme.spacing(1.25, 1.5),
            background: tile,
            border: `1px solid ${tileBorder}`,
            marginTop: theme.spacing(1),
            [theme.breakpoints.down('sm')]: {
                gridTemplateColumns: 'minmax(0,1fr) auto',
            },
        },
        fileCell: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            minWidth: 0,
        },
        fileInfo: {
            minWidth: 0,
            flex: 1,
        },
        extBadge: {
            width: 36,
            height: 42,
            flexShrink: 0,
            borderRadius: 6,
            background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            border: `1px solid ${tileBorder}`,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: 5,
            boxSizing: 'border-box',
            fontSize: 9,
            fontWeight: 700,
            color: accent,
        },
        fileName: {
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: textMain,
        },
        fileRemark: {
            fontSize: 11,
            color: textMuted,
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        cellMeta: {
            fontSize: 12,
            color: textMain,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },
        metaMobile: {
            fontSize: 11,
            color: textMuted,
            marginTop: 3,
            [theme.breakpoints.up('md')]: {
                display: 'none',
            },
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 4,
        },
        actionBtn: {
            padding: 6,
            border: `1px solid ${line}`,
            borderRadius: 6,
            '&:hover': {
                borderColor: accent,
                color: accent,
            },
        },
        emptyBox: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(6, 2),
            border: `1px dashed ${dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
            borderRadius: 10,
            textAlign: 'center',
        },
        emptyIcon: {
            color: textMuted,
            fontSize: 34,
        },
        emptyTitle: {
            fontSize: 14,
            fontWeight: 500,
            color: textMain,
        },
        emptyHint: {
            fontSize: 12,
            color: textMuted,
            marginBottom: theme.spacing(0.5),
        },
        skeletonRow: {
            borderRadius: 10,
            marginTop: theme.spacing(1),
        },
        dialogTitle: {
            fontSize: 16,
            fontWeight: 700,
        },
        dialogSubtitle: {
            fontSize: 11,
            color: textMuted,
            marginTop: 2,
        },
        dialogContent: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.75),
            paddingTop: 0,
        },
        hiddenInput: {
            display: 'none',
        },
        dropZone: {
            border: `2px dashed ${dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
            borderRadius: 12,
            padding: '32px 20px',
            textAlign: 'center',
            backgroundColor: 'rgba(254,242,0,0.02)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'border-color 0.2s, background-color 0.2s',
        },
        dropZoneHover: {
            borderColor: 'rgba(254,242,0,0.5)',
            backgroundColor: 'rgba(254,242,0,0.06)',
        },
        dropZoneIcon: {
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: 'rgba(254,242,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12,
        },
        dropZoneIconSvg: {
            color: accent,
            fontSize: 24,
        },
        dropZoneText: {
            color: textMain,
            fontWeight: 500,
        },
        dropZoneHint: {
            color: textMuted,
            marginTop: 6,
        },
        browseLink: {
            color: accent,
            fontWeight: 700,
            textDecoration: 'underline',
        },
        pendingRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1.25, 1.5),
            borderRadius: 8,
            background: tile,
            border: `1px solid ${tileBorder}`,
        },
        pendingInfo: {
            flex: 1,
            minWidth: 0,
        },
        pendingSize: {
            fontSize: 11,
            color: textMuted,
            marginTop: 2,
        },
        errorText: {
            fontSize: 12,
            color: red[400],
        },
        deleteTitleRow: {
            display: 'flex',
            alignItems: 'center',
        },
        warningIcon: {
            color: '#FEF200',
            marginRight: theme.spacing(1),
        },
        deleteContent: {
            paddingTop: 0,
        },
        deleteName: {
            fontWeight: 600,
        },
        deleteConfirmBtn: {
            backgroundColor: red[600],
            color: '#ffffff',
            '&:hover': {
                backgroundColor: red[700],
            },
        },
    };
});

function AttachmentRow({row, own, language, canView, downloading, onView, onDownload, onDelete}) {
    const handleView = () => onView(row);
    const handleDownload = () => onDownload(row);
    const handleDelete = () => onDelete(row);
    const hasRemark = row.remark !== null && row.remark !== undefined && String(row.remark).trim() !== '';
    const uploadedBy = row.created_by ? row.created_by : '-';
    const uploadedOn = formatUploadedOn(row.created_date);
    const size = formatFileSize(row.file_size);
    return (
        <div className={own.row}>
            <div className={own.fileCell}>
                <div className={own.extBadge}>{fileExt(row.file_name)}</div>
                <div className={own.fileInfo}>
                    <Tooltip title={row.file_name || ''}>
                        <div className={own.fileName}>{row.file_name}</div>
                    </Tooltip>
                    {hasRemark && <div className={own.fileRemark}>{row.remark}</div>}
                    <div className={own.metaMobile}>{`${uploadedBy} · ${uploadedOn} · ${size}`}</div>
                </div>
            </div>
            <div className={own.cellMeta}>{uploadedBy}</div>
            <div className={own.cellMeta}>{uploadedOn}</div>
            <div className={own.cellMeta}>{size}</div>
            <div className={own.actions}>
                {
                    canView && (
                        <Tooltip title={language.view}>
                            <IconButton size="small" className={own.actionBtn} onClick={handleView}>
                                <VisibilityIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )
                }
                <Tooltip title={language.download}>
                    <span>
                        <IconButton size="small" className={own.actionBtn} onClick={handleDownload} disabled={downloading}>
                            {downloading ? <CircularProgress size={16} color="inherit"/> : <GetAppIcon fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={language.delete}>
                    <IconButton size="small" className={own.actionBtn} onClick={handleDelete}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );
}

function UploadDialog({
    open, own, language, userName, userLogin, pendingFile, remark, uploadError, uploading, dragOver, fileInputRef,
    onClose, onDragOver, onDragLeave, onDrop, onBrowse, onInputChange, onClearFile, onRemarkChange, onSubmit
}) {
    const renderFooter = () => (
        <>
            <Button onClick={onClose} disabled={uploading} color="primary">
                {language.cancel}
            </Button>
            <Button
                onClick={onSubmit}
                disabled={!pendingFile || uploading}
                variant="contained"
                className={own.uploadBtn}
                startIcon={uploading ? <CircularProgress size={16} color="inherit"/> : null}>
                {uploading ? language.uploading : language.upload}
            </Button>
        </>
    );
    return (
        <AnyDialog
            open={open}
            onDismiss={onClose}
            maxWidth="sm"
            fullWidth
            title={
                <div>
                    <div className={own.dialogTitle}>{language.upload_attachment}</div>
                    <div className={own.dialogSubtitle}>{`${userName || ''} (${userLogin || ''})`}</div>
                </div>
            }
            footer={renderFooter}>
            <DialogContent className={own.dialogContent}>
                <input ref={fileInputRef} type="file" className={own.hiddenInput} onChange={onInputChange}/>
                {
                    !pendingFile && (
                        <div
                            className={`${own.dropZone} ${dragOver ? own.dropZoneHover : ''}`}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onClick={onBrowse}>
                            <div className={own.dropZoneIcon}>
                                <CloudUploadIcon className={own.dropZoneIconSvg}/>
                            </div>
                            <Typography variant="body2" className={own.dropZoneText}>
                                {language.drag_drop_hint} <span className={own.browseLink}>{language.browse}</span>
                            </Typography>
                            <Typography variant="caption" className={own.dropZoneHint}>
                                {language.max_file_size_hint}
                            </Typography>
                        </div>
                    )
                }
                {
                    pendingFile && (
                        <div className={own.pendingRow}>
                            <div className={own.extBadge}>{fileExt(pendingFile.name)}</div>
                            <div className={own.pendingInfo}>
                                <div className={own.fileName}>{pendingFile.name}</div>
                                <div className={own.pendingSize}>{formatFileSize(pendingFile.size)}</div>
                            </div>
                            <IconButton size="small" onClick={onClearFile} disabled={uploading}>
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </div>
                    )
                }
                {uploadError !== '' && <div className={own.errorText}>{uploadError}</div>}
                <TextField
                    label={language.remark_optional}
                    value={remark}
                    onChange={onRemarkChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{maxLength: 255}}
                    disabled={uploading}/>
            </DialogContent>
        </AnyDialog>
    );
}

function DeleteDialog({open, own, language, target, deleting, onClose, onConfirm}) {
    const renderFooter = () => (
        <>
            <Button onClick={onClose} disabled={deleting} color="primary">
                {language.cancel}
            </Button>
            <Button
                onClick={onConfirm}
                disabled={deleting}
                variant="contained"
                className={own.deleteConfirmBtn}
                startIcon={deleting ? <CircularProgress size={16} color="inherit"/> : null}>
                {language.delete}
            </Button>
        </>
    );
    return (
        <AnyDialog
            open={open}
            onDismiss={onClose}
            maxWidth="xs"
            title={
                <span className={own.deleteTitleRow}>
                    <WarningIcon className={own.warningIcon}/>
                    <Typography variant="h6" component="span">{language.delete_attachment_title}</Typography>
                </span>
            }
            footer={renderFooter}>
            <DialogContent className={own.deleteContent}>
                <Typography variant="body2">
                    <span className={own.deleteName}>{target ? target.file_name : ''}</span> {language.delete_attachment_message}
                </Typography>
            </DialogContent>
        </AnyDialog>
    );
}

export default function AttachmentTab({classes, loginId, userName, userLogin, language, axiosConfig, axiosPut, snack}) {
    const own = useStyles();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef(null);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [remark, setRemark] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [viewerIndex, setViewerIndex] = useState(null);
    const [savingRotation, setSavingRotation] = useState(false);

    const loadAttachments = () => {
        setLoading(true);
        const param = axiosURIencode({login_id: loginId});
        API_Service.get(`${req_manage_users_attachment}?${param}`, axiosConfig).then(({data}) => {
            const {result, message, extraData} = data;
            if (parseInt(result, 10) === 1) {
                setAttachments(Array.isArray(extraData) ? extraData : []);
            } else {
                snack(message || language.operation_fail, 'error');
            }
            setLoading(false);
            setLoaded(true);
        }).catch(() => {
            snack(language.network_error, 'error');
            setLoading(false);
            setLoaded(true);
        });
    };

    useEffect(() => {
        if (loginId) {
            setAttachments([]);
            setLoaded(false);
            loadAttachments();
        }
    }, [loginId, axiosConfig]);

    const resetUploadForm = () => {
        setPendingFile(null);
        setRemark('');
        setUploadError('');
        setDragOver(false);
    };

    const openUpload = () => {
        resetUploadForm();
        setUploadOpen(true);
    };

    const closeUpload = () => {
        if (uploading) return;
        setUploadOpen(false);
        resetUploadForm();
    };

    const handleFiles = (fileList) => {
        const file = fileList && fileList.length > 0 ? fileList[0] : null;
        if (!file) return;
        if (file.size > MAX_FILE_BYTES) {
            setPendingFile(null);
            setUploadError(language.file_too_large);
            return;
        }
        setUploadError('');
        setPendingFile(file);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const onBrowse = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const onInputChange = (e) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const onClearFile = () => {
        setPendingFile(null);
        setUploadError('');
    };

    const onRemarkChange = (e) => {
        setRemark(e.target.value);
    };

    const submitUpload = () => {
        if (!pendingFile || uploading) return;
        setUploading(true);
        const formdata = new FormData();
        formdata.append('login_id', loginId);
        formdata.append('file', pendingFile);
        formdata.append('remark', remark.trim());
        API_Service.post(req_manage_users_attachment, formdata, axiosConfig).then(({data}) => {
            const {result, message} = data;
            setUploading(false);
            if (parseInt(result, 10) === 1) {
                snack(language.attachment_uploaded, 'success');
                setUploadOpen(false);
                resetUploadForm();
                loadAttachments();
            } else {
                snack(message || language.upload_fail, 'error');
            }
        }).catch(() => {
            setUploading(false);
            snack(language.upload_fail, 'error');
        });
    };

    const askDelete = (row) => {
        setDeleteTarget(row);
    };

    const closeDelete = () => {
        if (deleting) return;
        setDeleteTarget(null);
    };

    const confirmDelete = () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        API_Service.put(req_manage_users_attachment, axiosURIencode({id: deleteTarget.id}), axiosPut).then(({data}) => {
            const {result, message} = data;
            setDeleting(false);
            if (parseInt(result, 10) === 1) {
                snack(language.attachment_deleted, 'success');
                setDeleteTarget(null);
                loadAttachments();
            } else {
                snack(message || language.delete_fail, 'error');
            }
        }).catch(() => {
            setDeleting(false);
            snack(language.delete_fail, 'error');
        });
    };

    const previewable = attachments.filter((row) => getPreviewKind(row) !== null);

    // text/csv previews: browsers refuse to render text/csv inline, so fetch via the download proxy
    const loadText = (row) => {
        const param = axiosURIencode({id: row.id});
        return API_Service.get(`${req_manage_users_attachment_download}?${param}`, axiosConfig).then(({data}) => {
            const {result, message, extraData} = data;
            if (parseInt(result, 10) === 1 && extraData && extraData.data) {
                const byteChars = atob(extraData.data);
                const byteArr = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                return new TextDecoder('utf-8').decode(byteArr);
            }
            throw new Error(message || language.preview_failed);
        });
    };

    const viewRow = (row) => {
        const idx = previewable.findIndex((r) => r.id === row.id);
        if (idx >= 0) setViewerIndex(idx);
    };

    const closeViewer = () => {
        if (savingRotation) return;
        setViewerIndex(null);
    };

    const saveRotation = (row, angle) => {
        if (savingRotation) return;
        setSavingRotation(true);
        const formdata = new FormData();
        formdata.append('id', row.id);
        formdata.append('angle', angle);
        API_Service.post(req_manage_users_attachment_rotate, formdata, axiosConfig).then(({data}) => {
            const {result, message, extraData} = data;
            setSavingRotation(false);
            if (parseInt(result, 10) === 1 && extraData && extraData.file_url) {
                setAttachments((prev) => prev.map((item) => (
                    item.id === row.id
                        ? {
                            ...item,
                            file_url: extraData.file_url,
                            file_name: extraData.file_name || item.file_name,
                            file_size: extraData.file_size || item.file_size,
                            content_type: extraData.content_type || item.content_type,
                        }
                        : item
                )));
                snack(language.rotation_saved, 'success');
            } else {
                snack(message || language.rotation_failed, 'error');
            }
        }).catch(() => {
            setSavingRotation(false);
            snack(language.rotation_failed, 'error');
        });
    };

    const downloadRow = (row) => {
        if (downloadingId) return;
        setDownloadingId(row.id);
        const param = axiosURIencode({id: row.id});
        API_Service.get(`${req_manage_users_attachment_download}?${param}`, axiosConfig).then(({data}) => {
            const {result, message, extraData} = data;
            if (parseInt(result, 10) === 1 && extraData && extraData.data) {
                try {
                    const byteChars = atob(extraData.data);
                    const byteArr = new Uint8Array(byteChars.length);
                    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                    const blob = new Blob([byteArr], {type: extraData.mime || row.content_type || 'application/octet-stream'});
                    saveAs(blob, extraData.file_name || row.file_name);
                    snack(language.download_success, 'success');
                } catch (e) {
                    snack(language.download_failed, 'error');
                }
            } else {
                snack(message || language.download_failed, 'error');
            }
            setDownloadingId(null);
        }).catch(() => {
            snack(language.download_failed, 'error');
            setDownloadingId(null);
        });
    };

    const count = attachments.length;
    const showSkeleton = loading && !loaded;
    const showEmpty = loaded && !loading && count === 0;
    const showList = loaded && count > 0;

    return (
        <div className={classes.infoContainer}>
            <div className={classes.sectionCard}>
                <div className={`${classes.sectionHeader} ${own.header}`}>
                    <div className={own.headerLeft}>
                        <div className={classes.sectionAccent}/>
                        <div className={classes.sectionIcon}><AttachmentIcon/></div>
                        <div className={classes.sectionTitle}>{language.attachments}</div>
                        <Chip size="small" label={`${count} ${language.files}`} className={own.countChip}/>
                    </div>
                    {
                        !showEmpty && (
                            <Button
                                variant="contained"
                                size="small"
                                className={own.uploadBtn}
                                startIcon={<CloudUploadIcon/>}
                                onClick={openUpload}
                                disabled={showSkeleton}>
                                {language.upload}
                            </Button>
                        )
                    }
                </div>
                {
                    showSkeleton && [0, 1, 2].map((i) => (
                        <Skeleton key={'atch-sk-' + i} variant="rect" height={60} animation="wave" className={own.skeletonRow}/>
                    ))
                }
                {loading && loaded && <LinearLoading/>}
                {
                    showEmpty && (
                        <div className={own.emptyBox}>
                            <AttachmentIcon className={own.emptyIcon}/>
                            <div className={own.emptyTitle}>{language.no_attachments_yet}</div>
                            <div className={own.emptyHint}>{language.no_attachments_hint}</div>
                            <Button
                                variant="contained"
                                size="small"
                                className={own.uploadBtn}
                                startIcon={<CloudUploadIcon/>}
                                onClick={openUpload}>
                                {language.upload}
                            </Button>
                        </div>
                    )
                }
                {
                    showList && !smDown && (
                        <div className={own.listHeader}>
                            <div className={classes.infoLabel}>{language.attachment}</div>
                            <div className={classes.infoLabel}>{language.uploaded_by}</div>
                            <div className={classes.infoLabel}>{language.uploaded_on}</div>
                            <div className={classes.infoLabel}>{language.file_size}</div>
                            <div className={`${classes.infoLabel} ${own.headerRight}`}>{language.action}</div>
                        </div>
                    )
                }
                {
                    showList && attachments.map((row) => (
                        <AttachmentRow
                            key={row.id}
                            row={row}
                            own={own}
                            language={language}
                            canView={getPreviewKind(row) !== null}
                            downloading={downloadingId === row.id}
                            onView={viewRow}
                            onDownload={downloadRow}
                            onDelete={askDelete}/>
                    ))
                }
            </div>
            <AttachmentViewer
                open={viewerIndex !== null}
                items={previewable}
                index={viewerIndex === null ? 0 : viewerIndex}
                language={language}
                onClose={closeViewer}
                onNavigate={setViewerIndex}
                onDownload={downloadRow}
                downloading={downloadingId !== null}
                onLoadText={loadText}
                onSaveRotation={saveRotation}
                savingRotation={savingRotation}/>
            <UploadDialog
                open={uploadOpen}
                own={own}
                language={language}
                userName={userName}
                userLogin={userLogin}
                pendingFile={pendingFile}
                remark={remark}
                uploadError={uploadError}
                uploading={uploading}
                dragOver={dragOver}
                fileInputRef={fileInputRef}
                onClose={closeUpload}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onBrowse={onBrowse}
                onInputChange={onInputChange}
                onClearFile={onClearFile}
                onRemarkChange={onRemarkChange}
                onSubmit={submitUpload}/>
            <DeleteDialog
                open={deleteTarget !== null}
                own={own}
                language={language}
                target={deleteTarget}
                deleting={deleting}
                onClose={closeDelete}
                onConfirm={confirmDelete}/>
        </div>
    );
}
